import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';
import { catchError, take, timeout } from 'rxjs/operators';

import { ErrorModalComponent } from '@app/shared/components/error-modal/error-modal.component';
import { BrowserStorageService } from '@app/services/browser-storage.service';
import { LoggerService } from '@app/core/logging/logger.service';
import { BrowserPage } from './browser-page';

/**
 * Marca de "ya recargué por un chunk que no existe". Va en `sessionStorage`
 * (por pestaña, y muere con ella) y se CONSUME al leerla.
 */
export const CHUNK_RELOAD_MARK_KEY = 'plp_chunk_reload_intentado';

/** Clave del aviso. Lo único que ve el usuario; del error no sale nada. */
export const CLAVE_AVISO = 'errors.appOutdated.message';

const VALOR_MARCA = '1';

/** Tope de espera del diccionario antes de abrir el aviso con lo que haya. */
const TOPE_TRADUCCION_MS = 3000;

/**
 * Recupera una pestaña que quedó con el `index.html` de un build viejo: sus
 * rutas perezosas piden chunks que ya no existen. Una recarga trae el
 * `index.html` nuevo con los hashes correctos — pero UNA sola.
 *
 * Si tras recargar vuelve a fallar, la recarga no es la cura (CDN con el
 * `index.html` viejo en caché, red caída, fichero roto) y se avisa con un
 * mensaje traducido en vez de recargar otra vez.
 */
@Injectable({ providedIn: 'root' })
export class ChunkLoadRecoveryService {
  private readonly storage = inject(BrowserStorageService);
  private readonly page = inject(BrowserPage);
  private readonly dialog = inject(MatDialog);
  private readonly transloco = inject(TranslocoService);
  private readonly logger = inject(LoggerService);

  /** Muere con la recarga que acaba de pedir; no es un latch de por vida. */
  private recargaSolicitada = false;

  /** Se apaga al cerrarse el aviso, o la pantalla volvería a morir en silencio. */
  private avisoEnPantalla = false;

  recover(): void {
    // En el servidor (SSR el config mergea el de la app) no hay página que
    // recargar ni usuario a quien avisar.
    if (!this.storage.isBrowser()) return;

    // De-duplicación de la RÁFAGA: un despliegue tumba varios chunks a la vez y
    // el router reintenta, así que el mismo fallo llega dos o tres veces en
    // milisegundos. Ninguna de las dos banderas es permanente.
    if (this.recargaSolicitada || this.avisoEnPantalla) return;

    if (!this.puedeRecargarSinBucle()) {
      this.logger.warn('[chunk] no se recarga (ya se intentó, o no hay freno demostrable): se avisa');
      this.avisar();
      return;
    }

    this.logger.warn('[chunk] chunk inexistente (build viejo en esta pestaña): se recarga una vez');
    this.recargaSolicitada = true;
    try {
      this.page.reload();
    } catch (error) {
      // Un `throw` SÍNCRONO dejaría la pestaña sin recarga y sin aviso, con la
      // bandera ya encendida. Hoy no es alcanzable —`SwUpdate.activateUpdate()`
      // devuelve una promesa rechazada, no lanza—, pero el aviso no puede
      // depender de un detalle de implementación ajeno.
      this.logger.error(error);
      this.recargaSolicitada = false;
      this.avisar();
    }
  }

  /**
   * Una recarga automática se permite solo si queda un freno DEMOSTRABLE para la
   * vuelta siguiente. Son dos mecanismos independientes y cada uno, por su
   * cuenta, corta el bucle.
   */
  private puedeRecargarSinBucle(): boolean {
    // A) Esta página ya ES el resultado de una recarga. No pasa por storage, así
    //    que aguanta un `sessionStorage.clear()` —la app hace nueve, y el del
    //    401 corre en cada carga con la sesión vencida— y aguanta un storage
    //    bloqueado.
    if (this.page.loadedByReload()) return false;

    // B) Esta pestaña ya recargó antes por esto (y después navegó, lo que deja
    //    el freno A en 'navigate' otra vez).
    if (this.consumirMarca()) return false;

    // C) Y la marca tiene que quedar escrita DE VERDAD. Sin esto el freno podía
    //    no existir y las recargas no tenían fin: `setSessionStorageItem` se
    //    come la excepción (`QuotaExceededError`, Tracking Prevention, modo
    //    privado) y `getSessionStorageItem` devuelve `null`, así que la marca no
    //    persistía, nadie se enteraba y cada carga volvía a recargar.
    return this.dejarMarcaVerificada();
  }

  /** Lee y borra la marca. `true` si esta pestaña ya había recargado por esto. */
  private consumirMarca(): boolean {
    const marca = this.storage.getSessionStorageItem(CHUNK_RELOAD_MARK_KEY);
    if (marca === null) return false;
    this.storage.removeSessionStorageItem(CHUNK_RELOAD_MARK_KEY);
    return true;
  }

  /**
   * Escribe la marca y la lee de vuelta. Se verifica el viaje completo y no
   * `isSessionStorageAvailable()` porque lo que importa es que persista ESTA
   * clave, no que una clave de sondeo se pueda escribir.
   */
  private dejarMarcaVerificada(): boolean {
    this.storage.setSessionStorageItem(CHUNK_RELOAD_MARK_KEY, VALOR_MARCA);
    const persistida = this.storage.getSessionStorageItem(CHUNK_RELOAD_MARK_KEY) === VALOR_MARCA;
    if (!persistida) {
      this.logger.warn('[chunk] la marca anti-bucle no persiste: no se recarga, se avisa');
    }
    return persistida;
  }

  private avisar(): void {
    // Se enciende ANTES de resolver la traducción: la ráfaga llega en
    // milisegundos y si no, salen dos diálogos apilados.
    this.avisoEnPantalla = true;
    this.storage.removeSessionStorageItem(CHUNK_RELOAD_MARK_KEY);

    this.mensajeDelAviso().subscribe((mensaje) => this.abrirAviso(mensaje));
  }

  /**
   * `translate()` es SÍNCRONO y sin diccionario cargado devuelve la CLAVE cruda.
   * No es hipotético: el inicializador de idioma solo hace `setActiveLang` y con
   * un usuario sin sesión en `/login` —ruta perezosa, o sea alcanzable por este
   * mismo fallo— todavía no se ha renderizado nada con `*transloco`. Mismo
   * motivo que `unassigned-domain-notice`, que ya usa `selectTranslate`.
   *
   * El respaldo cubre que el diccionario tampoco se pueda traer: ahí se abre el
   * aviso con lo que haya en vez de quedarse callado.
   */
  private mensajeDelAviso(): Observable<string> {
    return this.transloco.selectTranslate<string>(CLAVE_AVISO).pipe(
      take(1),
      timeout(TOPE_TRADUCCION_MS),
      catchError(() => of(this.transloco.translate<string>(CLAVE_AVISO)))
    );
  }

  private abrirAviso(mensaje: string): void {
    const referencia = this.dialog.open(ErrorModalComponent, {
      width: '400px',
      data: { message: mensaje },
    });

    // Al cerrarse se re-arma el aviso: un `ChunkLoadError` posterior en esta
    // pestaña tiene que volver a decir algo, no morir en silencio.
    referencia
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.avisoEnPantalla = false;
      });
  }
}
